/**
 * HypothalamusSensesCard — хипоталамус (терморегулация), сетивна проверка
 * и проверка на черепните нерви (Nerve4). Три таба в една карта.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Brain, Loader2, Siren, Thermometer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: "thermo", label_bg: "Температура" },
  { key: "senses", label_bg: "Сетива" },
  { key: "cranial", label_bg: "Черепни нерви" },
];

const Chips = ({ opts, value, onPick, name, multi = false, border }) => (
  <div className="mt-1 flex flex-wrap gap-1.5">
    {opts.map((o) => {
      const on = multi ? (value || []).includes(o.key) : value === o.key;
      return (
        <button key={o.key} type="button" onClick={() => onPick(o.key)}
          className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
            on ? "border-violet-500/50 bg-violet-500/15" : border}`}
          data-testid={`hypo-${name}-${o.key}`}>
          {o.label_bg}
        </button>
      );
    })}
  </div>
);

export const HypothalamusSensesCard = ({ d, setD, cardCls, border }) => {
  const h = d?.hypothalamus;
  const p = h?.protocol;
  const [tab, setTab] = useState(null);
  const [busy, setBusy] = useState(false);
  const [thermo, setThermo] = useState([]);
  const [sen, setSen] = useState({ smell: "all4", taste: "normal", hearing: "fine", vision: "fine", extras: [] });
  const [cr, setCr] = useState({ items: [], onset: "slow" });
  if (!p) return null;

  const post = async (url, body) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/neuro-twin/${url}`, body);
      setD(data); setTab(null); toast.success("Записано");
    } catch { toast.error("Записът не мина"); }
    finally { setBusy(false); }
  };
  const toggleIn = (arr, k) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  const t = h.thermoregulation;
  const s = h.senses;
  const c = h.cranial;

  return (
    <div className={cardCls} data-testid="neuro-hypothalamus-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Brain className="w-4 h-4 text-violet-500" /> Хипоталамус, сетива и черепни нерви
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-60" />}
          </div>
          <p className="text-[11px] opacity-75 mt-1">{h.explain_bg}</p>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {TABS.map((x) => (
          <button key={x.key} type="button"
            onClick={() => setTab(tab === x.key ? null : x.key)}
            className={`px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border ${
              tab === x.key ? "border-violet-500/50 bg-violet-500/15" : border}`}
            data-testid={`hypo-tab-${x.key}`}>
            {x.label_bg}
          </button>
        ))}
      </div>

      {c?.urgent && (
        <div className="mt-3 p-2.5 rounded-xl bg-rose-500/15 border border-rose-500/50"
          data-testid="hypo-cranial-urgent">
          <div className="text-[12px] font-bold text-rose-600 dark:text-rose-300 inline-flex items-center gap-1.5">
            <Siren className="w-4 h-4" /> Това е спешно
          </div>
          <p className="text-[11px] mt-1 font-medium">{c.urgent_text_bg}</p>
          <p className="text-[11px] mt-1 font-bold">{c.action_bg}</p>
        </div>
      )}

      {s?.urgent_bg?.length > 0 && (
        <div className="mt-2 p-2 rounded-lg border border-amber-500/50 bg-amber-500/10"
          data-testid="hypo-senses-urgent">
          {s.urgent_bg.map((x, i) => (
            <p key={i} className="text-[11px] font-medium">{x}</p>
          ))}
        </div>
      )}

      {t && (
        <div className={`mt-3 p-2 rounded-lg border ${border}`} data-testid="hypo-thermo-result">
          <div className="text-[12px] font-bold inline-flex items-center gap-1.5">
            <Thermometer className="w-3.5 h-3.5 text-violet-500" /> {t.summary_bg}
          </div>
          <p className="text-[11px] opacity-75 mt-0.5">{t.explain_bg}</p>
          {t.attribution.map((a) => (
            <div key={a.key} className="mt-1.5" data-testid={`hypo-thermo-${a.key}`}>
              <div className="text-[11px] font-bold">{a.title_bg}</div>
              <div className="text-[11px] opacity-80">{a.text_bg}</div>
              <div className="text-[11px] font-medium opacity-90">{a.action_bg}</div>
            </div>
          ))}
        </div>
      )}

      {s && (
        <div className={`mt-2 p-2 rounded-lg border ${border}`} data-testid="hypo-senses-result">
          <div className="text-[12px] font-bold">Сетивна проверка ({s.date})</div>
          <p className="text-[11px] opacity-75 mt-0.5">{s.explain_bg}</p>
          {s.findings.map((f) => (
            <div key={f.key} className="mt-1.5" data-testid={`hypo-sense-${f.key}`}>
              <div className="text-[11px] font-bold">{f.title_bg}</div>
              <div className="text-[11px] opacity-80">{f.text_bg}</div>
              <div className="text-[11px] font-medium opacity-90">{f.action_bg}</div>
            </div>
          ))}
          {s.findings.length === 0 && (
            <p className="text-[11px] opacity-75 mt-1">Няма находки — повторете след 6 месеца.</p>
          )}
        </div>
      )}

      {c && c.rows?.length > 0 && (
        <div className={`mt-2 p-2 rounded-lg border ${border}`} data-testid="hypo-cranial-result">
          <div className="text-[12px] font-bold">{c.summary_bg}</div>
          {c.rows.map((r, i) => (
            <div key={i} className="text-[11px] opacity-85 mt-0.5">
              {r.label_bg} <span className="opacity-60">— {r.nerve_bg}</span>
            </div>
          ))}
          <div className="text-[11px] font-medium mt-1">{c.action_bg}</div>
        </div>
      )}

      {tab === "thermo" && (
        <div className="mt-3" data-testid="hypo-thermo-form">
          <div className="text-[12px] font-bold">Какво от това Ви се случва?</div>
          <Chips opts={p.thermo_items} value={thermo} name="thermo" multi border={border}
            onPick={(k) => setThermo((a) => toggleIn(a, k))} />
          <button type="button" onClick={() => post("thermoregulation", { items: thermo })}
            disabled={busy}
            className="mt-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold disabled:opacity-60"
            data-testid="hypo-thermo-save">Запиши</button>
        </div>
      )}

      {tab === "senses" && (
        <div className="mt-3 space-y-2" data-testid="hypo-senses-form">
          {["smell", "taste", "hearing", "vision"].map((k) => (
            <div key={k} className={`p-2 rounded-lg border ${border}`}>
              <div className="text-[12px] font-bold">{p.senses[k].title_bg}</div>
              <div className="text-[10px] opacity-70 mt-0.5">{p.senses[k].how_bg}</div>
              <Chips opts={p.senses[k].opts} value={sen[k]} name={k} border={border}
                onPick={(v) => setSen((st) => ({ ...st, [k]: v }))} />
            </div>
          ))}
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Допълнително</div>
            <Chips opts={p.senses.extras} value={sen.extras} name="extras" multi border={border}
              onPick={(k) => setSen((st) => ({ ...st, extras: toggleIn(st.extras, k) }))} />
          </div>
          <button type="button" onClick={() => post("senses", sen)} disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold disabled:opacity-60"
            data-testid="hypo-senses-save">Запишете проверката</button>
        </div>
      )}

      {tab === "cranial" && (
        <div className="mt-3 space-y-2" data-testid="hypo-cranial-form">
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Имате ли нещо от това?</div>
            <Chips opts={p.cranial_items} value={cr.items} name="cranial" multi border={border}
              onPick={(k) => setCr((st) => ({ ...st, items: toggleIn(st.items, k) }))} />
          </div>
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Как се появи?</div>
            <Chips opts={p.cranial_onset} value={cr.onset} name="onset" border={border}
              onPick={(k) => setCr((st) => ({ ...st, onset: k }))} />
          </div>
          <button type="button" onClick={() => post("cranial-screen", cr)} disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold disabled:opacity-60"
            data-testid="hypo-cranial-save">Запиши</button>
        </div>
      )}

      <p className="text-[9px] opacity-50 mt-2">{h.note_bg}</p>
    </div>
  );
};

export default HypothalamusSensesCard;
